import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Cancel, Done } from '@material-ui/icons';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { IJournalEquipmentItem, IEquipment, IEquipmentType } from '../../interfaces';
import JournalEquipmentItemForm from './JournalEquipmentItemForm';

interface IProps {
  open: boolean;
  title: string;
  submitButtonText: string;
  equipmentItem: IJournalEquipmentItem | null;
  disabled?: boolean;
  onClose(): void;
  onSubmit(equipmentItem: IJournalEquipmentItem): void | Promise<void>;
}

export interface IFormValues {
  equipmentType?: IEquipmentType | null;
  equipment: IEquipment | null;
  equipmentCount: number;
  equipmentCountry: string;
  equipmentModel: string;
  equipmentSerialNumberItem: string;
  equipmentSerialNumber: string[];
  equipmentCost: number;
  dateStart: Date | null;
  monthCount: number;
  comment: string;
  guaranteeMonth: number;
  equipmentTypeName?: string;
}

const BaseJournalEquipmentItemDialog: React.FC<IProps> = ({
  open,
  title,
  submitButtonText,
  equipmentItem,
  disabled = false,
  onClose,
  onSubmit,
}) => {
  const initialValues: IFormValues = {
    equipmentType: null,
    equipment: null,
    equipmentCount: 0,
    equipmentCountry: '',
    equipmentModel: '',
    equipmentSerialNumberItem: '',
    equipmentSerialNumber: [],
    equipmentCost: 0,
    dateStart: null,
    monthCount: 0,
    comment: '',
    guaranteeMonth: 0,
    equipmentTypeName: equipmentItem?.equipmentTypeName || '',
  };

  const validationSchema = yup.object().shape({
    equipment: yup
      .object()
      .required('Обязательное поле')
      .nullable()
      .shape({ equipmentId: yup.string().required('Обязательное поле') }),
    equipmentCount: yup.number().required('Обязательное поле').min(1, 'Минимум 1'),
    equipmentCountry: yup.string().required('Обязательное поле'),
    equipmentModel: yup.string().required('Обязательное поле'),
    equipmentSerialNumber: yup.array().of(yup.string()),
    equipmentCost: yup.number().required('Обязательное поле'),
    dateStart: yup.date().nullable().typeError('Укажите корректную дату').required('Обязательное поле'),
    monthCount: yup.number().required('Обязательное поле'),
    comment: yup.string().nullable().required('Обязательное поле'),
    guaranteeMonth: yup.number().min(1, 'Обязательное поле').required('Обязательное поле'),
  });

  const handleSubmit = async (values: IFormValues) => {
    const { equipment, equipmentType, equipmentSerialNumber, equipmentSerialNumberItem, equipmentTypeName, ...props } =
      values;

    await onSubmit({
      journalEquipmentItemId: equipmentItem?.journalEquipmentItemId,
      typeTechnicalRoomId: equipmentItem?.typeTechnicalRoomId,
      equipmentId: equipment?.equipmentId,
      equipmentTypeId: equipmentType?.equipmentTypeId || equipmentItem?.equipmentTypeId,
      equipmentSerialNumber: equipmentSerialNumberItem
        ? [...equipmentSerialNumber, equipmentSerialNumberItem]
        : equipmentSerialNumber,
      ...props,
    });
  };

  // Преобразуем equipmentItem в IFormValues если он существует
  const getInitialValues = (): IFormValues => {
    // Если нет equipmentItem или это новая запись (нет journalEquipmentItemId), используем начальные значения
    if (!equipmentItem || !equipmentItem.journalEquipmentItemId) {
      return {
        ...initialValues,
        equipmentTypeName: equipmentItem?.equipmentTypeName || '',
      };
    }

    return {
      equipmentType: equipmentItem.equipmentType || null,
      equipment: equipmentItem.equipment || null,
      equipmentCount: equipmentItem.equipmentCount || 0,
      equipmentCountry: equipmentItem.equipmentCountry || '',
      equipmentModel: equipmentItem.equipmentModel || '',
      equipmentSerialNumberItem: '',
      equipmentSerialNumber: equipmentItem.equipmentSerialNumber || [],
      equipmentCost: equipmentItem.equipmentCost || 0,
      dateStart: equipmentItem.dateStart || null,
      monthCount: equipmentItem.monthCount || 0,
      comment: equipmentItem.comment || '',
      guaranteeMonth: equipmentItem.guaranteeMonth || 0,
      equipmentTypeName: equipmentItem.equipmentTypeName || '',
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle>
        <b>{title}</b>
      </DialogTitle>

      <DialogContent dividers>
        <Formik<IFormValues>
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          initialValues={getInitialValues()}
          enableReinitialize
        >
          <Form>
            <JournalEquipmentItemForm
              disabled={disabled}
              equipmentTypeId={equipmentItem?.equipmentTypeId}
            />

            <DialogActions style={{ marginTop: 16, padding: 0 }}>
              <Button variant="outlined" onClick={onClose} disabled={disabled} startIcon={<Cancel />}>
                Отмена
              </Button>

              <Button color="primary" variant="contained" type="submit" disabled={disabled} startIcon={<Done />}>
                {submitButtonText}
              </Button>
            </DialogActions>
          </Form>
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default BaseJournalEquipmentItemDialog;
